<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { linePath, formatPercent } from '../../helpers/operationsChartHelper';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    default: '—',
  },
  // Percentage change against the previous period; null when there is nothing to compare against.
  delta: {
    type: Number,
    default: null,
  },
  // Which direction is good news for this metric: more conversations is neither, a faster first
  // response is, so the same arrow gets a different colour depending on the metric.
  goodDirection: {
    type: String,
    default: null,
  },
  series: {
    type: Array,
    default: () => [],
  },
  tone: {
    type: String,
    default: 'blue',
  },
});

const { t, locale } = useI18n();

const SPARK_WIDTH = 92;
const SPARK_HEIGHT = 30;

const sparkline = computed(() => {
  const values = props.series.filter(value => Number.isFinite(value));
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values.map((value, index) => [
    (SPARK_WIDTH * index) / (values.length - 1),
    SPARK_HEIGHT - 3 - ((SPARK_HEIGHT - 6) * (value - min)) / span,
  ]);

  return {
    line: linePath(points),
    area: `${linePath(points)} L${SPARK_WIDTH} ${SPARK_HEIGHT} L0 ${SPARK_HEIGHT} Z`,
    last: points[points.length - 1],
  };
});

const deltaTone = computed(() => {
  if (props.delta === null || !props.goodDirection) return 'text-n-slate-10';

  const rising = props.delta > 0;
  const good = props.goodDirection === 'up' ? rising : !rising;
  return good ? 'text-n-teal-11' : 'text-n-ruby-11';
});

const strokeClass = computed(() =>
  props.tone === 'teal' ? 'stroke-n-teal-9' : 'stroke-n-blue-9'
);
const fillClass = computed(() =>
  props.tone === 'teal' ? 'fill-n-teal-9' : 'fill-n-blue-9'
);
</script>

<template>
  <div class="flex flex-col p-4 border rounded-xl border-n-weak bg-n-solid-2">
    <p class="mb-1.5 text-xs text-n-slate-11">{{ label }}</p>
    <div class="text-2xl font-semibold tracking-tight text-n-slate-12">
      {{ value }}
    </div>
    <div class="flex items-end justify-between gap-2 mt-2.5">
      <span class="text-xs font-medium" :class="deltaTone">
        <template v-if="delta === null">
          {{ t('OPERATIONS_REPORTS.KPI.NO_COMPARISON') }}
        </template>
        <template v-else>
          {{ delta > 0 ? '▲' : '▼' }}
          {{ formatPercent(Math.abs(delta), locale) }}%
        </template>
      </span>
      <svg
        v-if="sparkline"
        :width="SPARK_WIDTH"
        :height="SPARK_HEIGHT"
        :viewBox="`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`"
        aria-hidden="true"
      >
        <path :d="sparkline.area" :class="fillClass" opacity="0.1" />
        <path
          :d="sparkline.line"
          fill="none"
          :class="strokeClass"
          stroke-width="1.75"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <circle
          :cx="sparkline.last[0]"
          :cy="sparkline.last[1]"
          r="2.5"
          :class="fillClass"
        />
      </svg>
    </div>
  </div>
</template>
